import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useConfig } from '../../contexts/ConfigContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import ticketService from '../../services/ticketService';
import counterServiceAPI from '../../services/counterService'; // Renommé pour éviter le conflit
 

// Modifications à apporter à client/src/pages/CounterApp/index.jsx

// Ajoutez les imports pour l'authentification
import { useNavigate } from 'react-router-dom';
import AuthCheck from '../../components/AuthCheck.jsx';
import axios from 'axios';
import { toast } from 'react-toastify';

/**
 * LIBELLÉ DU TICKET — celui que le SERVEUR compose, et personne d'autre.
 *
 * Quatre surfaces montrent le même ticket : l'écran, la fenêtre d'appel, le guichet et le
 * papier. Chacune le composait de son côté, et elles ont fini par ne plus dire la même
 * chose — le papier annonçait `ACC20` quand l'écran affichait `إستقبال 20`. Le serveur
 * compose désormais une fois et attache `label` au ticket ; le guichet l'affiche tel quel.
 *
 * Replis en cascade pour les serveurs pas encore à jour : identifiant technique, puis
 * numéro nu. Un guichet qui n'affiche rien ne se rapproche de rien.
 */
const libelleTicket = (ticket) => {
  if (!ticket) return '-';
  return ticket.label || ticket.ticketId || ticket.number || '-';
};

/**
 * Application pour les guichetiers
 * Permet d'appeler les clients et de gérer le service
 */
const CounterApp = () => {
  const { config, loading: configLoading, error: configError } = useConfig();
  const { socket, connected, registerClient } = useSocket();
  const navigate = useNavigate();
  // `logoutCompte` (le CONTEXTE) ≠ `logout` (plus bas, local) : ce dernier ne déconnecte pas le
  // compte, il QUITTE LE GUICHET (ferme la socket, libère le ticket actif). Deux choses très
  // différentes sous un nom presque identique — d'où le nommage explicite ici.
  const { logout: logoutCompte } = useAuth();

  
  // États
  const [user, setUser] = useState(null);
  // const [user, setUser] = useState(JSON.parse(localStorage.getItem('user')));
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [selectedCounter, setSelectedCounter] = useState(null);
  // const [selectedCounter, setSelectedCounter] = useState(JSON.parse(localStorage.getItem('selectedCounter')));
  // const [selectedCounter, setSelectedCounter] = useState(null); 

  const [activeTicket, setActiveTicket] = useState(null);
  const [waitingTickets, setWaitingTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    servedClients: 0,
    averageServiceTime: 0
  });
  const [waitingCount, setWaitingCount] = useState(0);
  // Numéro d'ordre global saisi par le guichetier pour appeler un client dont le tour est
  // passé (son papier le porte). Vide le reste du temps.
  const [numeroGlobal, setNumeroGlobal] = useState('');

  const [ticketsByServiceType, setTicketsByServiceType] = useState({});
  // États d'affichage temps réel (alimentés par updateDisplayData).
  const [servingTickets, setServingTickets] = useState([]);
  const [waitingCounts, setWaitingCounts] = useState({});
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  // Heure du dernier rechargement de la FILE. Affichée sous le compteur : un guichetier doit
  // pouvoir voir d'un coup d'œil que sa file est vivante — et nous, constater qu'elle se
  // rafraîchit bien quand un AUTRE guichet appelle.
  const [fileRafraichieA, setFileRafraichieA] = useState(null);
  // Rappel des raccourcis clavier. Ils existent depuis toujours (F2–F6) mais RIEN ne le
  // disait au guichetier : une fonctionnalité que personne ne découvre n'existe pas.
  const [aideRaccourcis, setAideRaccourcis] = useState(false);
  /**
   * Rafraîchissement de la file locale, appelé par les écouteurs temps réel.
   *
   * Passe par une REF, et non par les dépendances de l'effet : les écouteurs se
   * réenregistraient à chaque rendu, et entre le `socket.off` du nettoyage et le
   * `socket.on` suivant il n'y avait AUCUN écouteur. Un appel tombé dans cette
   * fenêtre était perdu sans trace. La ref garde les fonctions à jour sans jamais
   * détacher les écouteurs.
   */
  const requestUpdateRef = useRef(() => {});
  /** Regroupe les rafales d'événements (250 ms) : dix appels ne font qu'un rechargement. */
  const rafraichirTimerRef = useRef(null);
  // Miroirs des états lus DEPUIS les écouteurs socket. Sans eux il faudrait les mettre en
  // dépendance de l'effet, donc réenregistrer les écouteurs à chaque changement — ce que
  // l'on vient précisément de supprimer.
  const selectedCounterRef = useRef(null);
  const activeTicketRef = useRef(null);

  useEffect(() => {
    const loadAllWaitingTickets = async () => {
      try {
        const result = {};
        for (const st of config.serviceTypes) {
          const response = await ticketService.getWaitingTickets(st.id);
          if (response.success) {
            result[st.id] = response.data.length;
          } else {
            result[st.id] = 0;
          }
        }
        setTicketsByServiceType(result);
      } catch (err) {
        console.error("Erreur chargement file d'attente globale:", err);
      }
    };
  
    if (config?.serviceTypes) {
      loadAllWaitingTickets();
    }
  }, [config]);
  // const [ticketsByServiceType, setTicketsByServiceType] = useState({});  


  /**
   * ÉCOUTE TEMPS RÉEL DU GUICHET — un seul effet, des écouteurs nommés.
   *
   * Trois défauts corrigés ici :
   *
   * 1. Le bloc était enregistré DEUX FOIS, dans deux effets successifs, et le nettoyage
   *    du second retirait aussi les écouteurs du premier.
   * 2. `socket.off('ticketCancelled')` sans référence de fonction retire TOUS les
   *    écouteurs de l'événement, y compris ceux d'un autre composant. On retire
   *    désormais nommément les siens.
   * 3. Surtout : le guichet n'écoutait NI l'appel NI la fin d'un ticket par un AUTRE
   *    guichet. Sa file locale n'était donc jamais corrigée — deux guichets sur une même
   *    file affichaient chacun un « Prochain » figé sur un ticket parti depuis longtemps,
   *    et le guichetier l'annonçait à voix haute. C'est le défaut le plus visible en salle.
   *
   * Les dépendances sont réduites au strict nécessaire (`socket`, `connected`, `user`) :
   * tout ce qui change souvent passe par `rafraichirFileRef`.
   */
  useEffect(() => {
    if (!socket) return undefined;

    registerClient('counter', { counterId: selectedCounterRef.current?.id, userId: user?.id });

    /**
     * Recharge la file DEPUIS LE SERVEUR, en regroupant les rafales (250 ms).
     *
     * Appel direct, sans indirection : `selectedCounter` est dans les dépendances de
     * l'effet, donc cette fonction voit toujours le bon guichet. Le poste ne change que
     * quand l'agent en change — les écouteurs ne se réenregistrent donc pas en continu,
     * ce qui était le seul motif de passer par une ref.
     */
    const planifierRafraichissement = () => {
      if (rafraichirTimerRef.current) return;
      rafraichirTimerRef.current = setTimeout(async () => {
        rafraichirTimerRef.current = null;
        // Relu MAINTENANT, et non capturé à l'enregistrement : la minuterie survit
        // volontairement au réenregistrement de l'effet, elle doit donc lire l'état du
        // moment où elle s'exécute — pas celui d'il y a 250 ms.
        const guichet = selectedCounterRef.current;
        if (!guichet) return;
        try {
          // Guichet « Tous les services » (serviceType vide) : file AGRÉGÉE de tous les services
          // (getWaitingTickets sans argument → /waiting). Sinon, la file du service du guichet.
          const r = await ticketService.getWaitingTickets(guichet.serviceType || undefined);
          setFileRafraichieA(new Date());
          if (r?.success && Array.isArray(r.data)) {
            // LES DEUX ensemble : la liste (d'où sort « Prochain ») et le compteur. Ils
            // étaient tenus séparément, et seul le compteur bougeait — « Prochain » restait
            // figé sur un ticket appelé par un autre guichet une minute plus tôt.
            setWaitingTickets(r.data);
            setWaitingCount(r.data.length);
          }
        } catch {
          // Une file non rafraîchie n'est pas une panne à annoncer : le prochain
          // événement, ou le relevé périodique, la remettra d'aplomb.
        }
        requestUpdateRef.current();
      }, 250);
    };

    const surAnnulation = (ticket) => {
      // Le ticket actif annulé ailleurs (console, admin) doit disparaître de l'écran.
      if (ticket?.ticketId && activeTicketRef.current?.ticketId === ticket.ticketId) {
        setActiveTicket(null);
      }
      planifierRafraichissement();
    };

    // Un ticket appelé, terminé ou rappelé — par CE guichet ou par un autre — change la
    // file de tout le monde. On recharge depuis le serveur plutôt que de rafistoler la
    // liste locale : une seule source de vérité, et plus de « Prochain » fantôme.
    const surMouvement = () => planifierRafraichissement();

    socket.on('ticketCancelled', surAnnulation);
    socket.on('newTicket', surMouvement);
    socket.on('ticketCalled', surMouvement);
    socket.on('ticketCompleted', surMouvement);
    socket.on('ticketRecalled', surMouvement);

    return () => {
      socket.off('ticketCancelled', surAnnulation);
      socket.off('newTicket', surMouvement);
      socket.off('ticketCalled', surMouvement);
      socket.off('ticketCompleted', surMouvement);
      socket.off('ticketRecalled', surMouvement);
      // On NE TOUCHE PAS au rafraîchissement en attente. C'était le défaut : entre
      // l'événement et les 250 ms de regroupement, le moindre re-rendu qui change une
      // dépendance rejoue cet effet — et son nettoyage annulait la minuterie, jamais
      // réarmée ensuite. Or un ticket appelé ailleurs déclenche précisément une cascade de
      // re-rendus (compteurs d'en-tête, horodatage). Le rafraîchissement était donc tué par
      // ce qui aurait dû le provoquer. La minuterie vit dans une ref : elle survit au
      // réenregistrement, et sa fonction relit le guichet courant au moment de s'exécuter.
    };
  }, [socket, connected, registerClient, user, selectedCounter]);

  
  // Récupérer l'utilisateur du localStorage au chargement
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);

        // Guichet habituel : on le PRÉ-SÉLECTIONNE, on n'y entre pas.
        //
        // Ce code ouvrait directement la session (`setIsLoggedIn(true)`) d'après le seul
        // `assignedCounterId` conservé dans le navigateur. Conséquence observée : après avoir
        // quitté un guichet pour en changer, un simple F5 rouvrait l'ANCIEN — le guichetier se
        // retrouvait devant un guichet qu'il n'avait pas choisi, à appeler des clients qui ne
        // sont pas les siens. Une préférence enregistrée dans un navigateur ne peut pas primer
        // sur le geste que l'agent vient de faire : elle propose, elle ne décide pas.
        if (parsedUser.assignedCounterId) {
          setSelectedCounter({ id: parsedUser.assignedCounterId });
        }
      } catch (err) {
        console.error('Erreur lors du parsing des données utilisateur:', err);
        // Session illisible/corrompue : on la coupe PAR LE CONTEXTE. Purger `localStorage` seul
        // laissait `AuthContext` avec un utilisateur fantôme en mémoire et l'en-tête axios encore
        // armé — une session à moitié morte, la pire des deux.
        logoutCompte();
        navigate('/login', { state: { from: '/counter' } });
      }
    } else {
      // Rediriger vers la page de connexion si non connecté
      navigate('/login', { state: { from: '/counter' } });
    }
  }, [navigate]);


  // Réparer un selectedCounter PARTIEL : l'auto-login via `assignedCounterId` le pose avec
  // seulement { id } (sans serviceType/name). Or callNextTicket/loadWaitingTickets utilisent
  // `selectedCounter.serviceType` → au 1er clic il vaut `undefined` → « Aucun ticket » (il
  // fallait re-sélectionner le guichet dans la liste pour obtenir l'objet complet). On le
  // complète ici depuis config.counters dès que la config est disponible.
  useEffect(() => {
    if (selectedCounter && !selectedCounter.serviceType && config?.counters) {
      const full = config.counters.find((c) => String(c.id) === String(selectedCounter.id));
      if (full) setSelectedCounter(full);
    }
  }, [config, selectedCounter]);

  // Charger les tickets en attente lors de la connexion à un guichet
  useEffect(() => {
    if (selectedCounter && isLoggedIn) {
      loadWaitingTickets();
      loadCounterStatus();

      // Enregistrer le client avec l'ID du guichet
      if (socket && connected) {
        socket.emit('counterLogin', { counterId: selectedCounter.id });
      }
    }
  }, [selectedCounter, isLoggedIn, socket, connected]);

  // Charger les tickets en attente pour le service correspondant au guichet
  const loadWaitingTickets = async () => {
    if (!selectedCounter) return;
    
    try {
      setLoading(true);
      const response = await ticketService.getWaitingTickets(selectedCounter.serviceType);
      
      if (response.success) {
        setWaitingTickets(response.data);
        setWaitingCount(response.data.length);
      } else {
        setError('La file n\'a pas pu être chargée. Elle se rafraîchira toute seule au prochain mouvement.');
      }
    } catch (err) {
      console.error('Erreur lors du chargement des tickets:', err);
      setError('Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.');
    } finally {
      setLoading(false);
    }
  };

  // Charger le statut actuel du guichet
  /**
   * REPREND le ticket que ce guichet est en train de servir, s'il y en a un.
   *
   * Le serveur fait foi : `/api/tickets/active` rend les tickets `serving` et
   * `serving_paused`, chacun avec son `counterId`. C'est l'usage même pour lequel cette
   * route existe (« vue admin Tickets actifs, guichet »), et le seul endroit où l'on peut
   * savoir qu'un client est encore en cours de service à ce guichet.
   *
   * Sans cette reprise, un ticket appelé et non terminé restait bloqué : le guichetier
   * revenait devant un guichet qui semblait libre, alors que le serveur le tenait pour
   * occupé — et le client, lui, attendait toujours qu'on le rappelle.
   */
  const reprendreTicketEnCours = async () => {
    if (!selectedCounter) return;
    try {
      const r = await axios.get('/api/tickets/active');
      const aMoi = (r.data?.data || [])
        .filter((t) => String(t.counterId) === String(selectedCounter.id))
        // Le plus récent d'abord : s'il y en a plusieurs de bloqués, on les solde un par
        // un en commençant par le dernier appelé.
        .sort((a, b) => new Date(b.calledAt || b.createdAt || 0) - new Date(a.calledAt || a.createdAt || 0));

      if (!aMoi.length) return;

      const enCours = aMoi[0];
      setActiveTicket(enCours);
      console.log(`[guichet] ticket repris : ${libelleTicket(enCours)} (${enCours.status})`);

      // Un ticket resté ouvert d'un jour précédent doit être ANNONCÉ, pas subi : le
      // guichetier verrait sinon un client « en cours » qu'il n'a jamais appelé, et son
      // guichet refuserait d'appeler le suivant sans qu'il comprenne pourquoi. La reprise
      // n'est pas limitée au jour même — un ticket oublié hier resterait bloqué à jamais.
      const appele = new Date(enCours.calledAt || enCours.createdAt || Date.now());
      if (appele.toDateString() !== new Date().toDateString()) {
        toast.warning(
          `Ticket ${libelleTicket(enCours)} resté en cours depuis le ${appele.toLocaleDateString('fr-FR')} `
          + `— terminez-le pour libérer le guichet.`,
        );
      }
      if (aMoi.length > 1) {
        console.warn(`[guichet] ${aMoi.length} tickets encore en cours sur ce guichet`);
      }
    } catch (err) {
      // Reprise impossible : on n'empêche pas d'ouvrir le guichet pour autant.
      console.warn('[guichet] reprise du ticket en cours impossible :', err?.message || err);
    }
  };

  /**
   * Recharge « Clients servis » et « Temps moyen » DEPUIS LE SERVEUR.
   *
   * Ces deux nombres étaient tenus dans l'état React du composant et incrémentés à la main
   * à chaque service : ils repartaient de zéro au moindre rechargement de page ou changement
   * de poste, et le « temps moyen » affichait en réalité la durée du DERNIER ticket — d'où
   * des valeurs aberrantes (228 min pour un client, puis 3 min pour deux).
   *
   * Le serveur, lui, sait les recalculer exactement : `counterPerformance` agrège les
   * tickets terminés du jour par guichet. Une seule source, et elle survit à tout.
   */
  const chargerStatistiquesGuichet = useCallback(async () => {
    const guichet = selectedCounterRef.current;
    if (!guichet) return;
    try {
      const reponse = await counterServiceAPI.getDailyPerformance();
      const perf = reponse?.data?.counters?.[guichet.id] || reponse?.data?.counters?.[String(guichet.id)];
      setStats({
        servedClients: perf?.servedClients || 0,
        averageServiceTime: perf?.averageServiceTime || 0,   // secondes
      });
    } catch {
      // Statistiques indisponibles : on n'alarme pas le guichetier pour un compteur.
      // Le service, lui, continue.
    }
  }, []);

  const loadCounterStatus = async () => {
    if (!selectedCounter) return;
    
    try {
      setLoading(true);
      const response = await counterServiceAPI.getCounterStatus(selectedCounter.id);
      
      if (response.success) {
        // Les statistiques ne viennent PLUS de `dailyStats` (jamais incrémenté côté serveur,
        // donc toujours nul) : elles sont recalculées par le serveur depuis les tickets
        // terminés du jour. Voir `chargerStatistiquesGuichet`.
        chargerStatistiquesGuichet();
        
        // Si le guichet a un ticket en cours, le récupérer.
        //
        // `currentTicket` n'existe PAS dans la réponse : `GET /counters/:id` renvoie
        // l'enregistrement du guichet, qui porte `currentTicketId` — un identifiant, pas
        // un objet. Cette condition était donc toujours fausse, et le ticket en cours
        // n'était jamais repris. Conséquence sur le terrain : un ticket appelé que le
        // guichetier n'avait pas terminé — parce qu'il a changé de guichet, rechargé la
        // page ou fermé le navigateur — restait `serving` côté serveur, invisible côté
        // guichet. Ni terminable, ni rappelable : un client en suspens, et un guichet
        // que le serveur croit occupé.
        if (response.data.currentTicket) {
          setActiveTicket(response.data.currentTicket);
        } else {
          await reprendreTicketEnCours();
        }
      } else {
        setError('L\'état du guichet n\'a pas pu être lu. Le service reste possible.');
      }
    } catch (err) {
      console.error('Erreur lors du chargement du statut du guichet:', err);
      setError('Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.');
    } finally {
      setLoading(false);
    }
  };

  // Se connecter à un guichet
  const loginToCounter = (counter) => {
    setSelectedCounter(counter);
    setIsLoggedIn(true);
  };

  // Se déconnecter du guichet
  const logout = () => {
    if (activeTicket) {
      if (!window.confirm("Vous avez un ticket actif. Voulez-vous vraiment vous déconnecter?")) {
        return;
      }
    }
    
    // Déconnexion du WebSocket
    if (socket && selectedCounter) {
      socket.emit('counterLogout', { counterId: selectedCounter.id });
    }
    
    setIsLoggedIn(false);
    setSelectedCounter(null);
    setActiveTicket(null);
    setWaitingTickets([]);
  };

  // Appeler le prochain ticket
  const callNextTicket = async () => {
    if (!selectedCounter || activeTicket) return;
    
    try {
      setLoading(true);
      // Guichet « Tous les services » (serviceType vide) : on appelle le ticket le PLUS ANCIEN de la
      // file agrégée, en passant SON service à l'API (le serveur appelle par service). `waitingTickets`
      // est trié du plus ancien au plus récent → [0] = prochain.
      const serviceAAppeler = selectedCounter.serviceType || waitingTickets[0]?.serviceType;
      if (!serviceAAppeler) {
        setError(null);
        toast.info('Aucun client en attente.');
        await publierStatutGuichet('available');
        setLoading(false);
        return;
      }
      const response = await ticketService.callNextTicket(
        selectedCounter.id,
        serviceAAppeler
      );
      
      // File vide : ce n'est PAS une panne. Le serveur répond 204 sans corps — donc un
      // corps VIDE, et non un objet. On accepte les trois formes que peut prendre ce
      // « rien » (drapeau explicite, succès sans données, réponse vide) : le guichet ne
      // doit pas dépendre de la version exacte de la couche service pour distinguer
      // « personne n'attend » de « le système est en panne ».
      const rienAAppeler = response?.fileVide
        || (response?.success && !response?.data)
        || !response
        || response === '';
      if (rienAAppeler) {
        setError(null);
        toast.info('Aucun client en attente pour ce service.');
        await publierStatutGuichet('available');
      } else if (response.success) {
        setError(null);                 // efface un éventuel bandeau resté d'un geste précédent
        setActiveTicket(response.data);
        // Retirer ce ticket de la liste d'attente
        setWaitingTickets(prev => prev.filter(t => t.ticketId !== response.data.ticketId));
        setWaitingCount(prev => Math.max(0, prev - 1));
        // Le serveur doit savoir que ce guichet est occupé (supervision, écran).
        publierStatutGuichet('busy');

        // Déclencher une mise à jour immédiate pour tous les écrans via Socket.io
        if (socket && connected) {
          socket.emit('forceDisplayUpdate');
        }
      } else {
        setError(response.error || "L'appel n'a pas abouti. Réessayez.");
      }
    } catch (err) {
      // Ce bloc enchaînait QUATRE `setError` successifs : le dernier écrasait les trois
      // autres, et un « aucun ticket en attente » ressortait en « erreur de connexion au
      // serveur ». Un seul chemin par situation, désormais, et un message qui dit au
      // guichetier ce qu'il doit faire — il a un client devant lui.
      console.error('Appel du prochain ticket :', err.response?.data || err.message);
      const statut = err.response?.status;
      const messageServeur = err.response?.data?.error || err.response?.data?.message;

      if (statut === 404) {
        // Pas une panne : la file s'est vidée entre l'affichage et le clic.
        setError(null);
        toast.info('Aucun client en attente pour ce service.');
        await publierStatutGuichet('available');
      } else if (messageServeur === 'Le guichet est déjà occupé' || statut === 409) {
        setError(null);
        toast.warning('Terminez d\'abord le ticket en cours.');
      } else if (err.response) {
        // Le serveur a répondu et refuse : son message est circonstancié, on le garde.
        setError(messageServeur || "L'appel n'a pas abouti. Réessayez.");
      } else {
        setError('Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * STATUT DU GUICHET poussé au SERVEUR.
   *
   * Le statut affiché en bas d'écran était déduit localement (`activeTicket ? busy :
   * available`) et ne quittait jamais le navigateur : la supervision et l'écran voyaient
   * un guichet éternellement dans l'état où il s'était enregistré. On informe donc le
   * serveur à chaque changement — sans jamais bloquer le guichetier si l'appel échoue :
   * un statut non transmis ne doit pas empêcher d'appeler le client suivant.
   */
  const publierStatutGuichet = useCallback(async (statut) => {
    if (!selectedCounter) return;
    try {
      await counterServiceAPI.updateCounterStatus(selectedCounter.id, statut);
      if (socket && connected) {
        socket.emit('counterStatusChanged', { counterId: selectedCounter.id, status: statut });
      }
    } catch (err) {
      console.warn('[guichet] statut non transmis au serveur :', err?.message || err);
    }
  }, [selectedCounter, socket, connected]);

  /**
   * Appeler un ticket par son NUMÉRO D'ORDRE GLOBAL (le client dont le tour est passé).
   *
   * Le serveur refuse un ticket déjà servi, annulé ou en cours ailleurs, avec une phrase
   * qui dit lequel des cas s'applique et à quelle heure. Cette phrase est faite pour être
   * lue au client : on l'affiche telle quelle, sans la reformuler.
   */
  const callGlobalTicket = async () => {
    const saisie = String(numeroGlobal || '').trim();
    if (!selectedCounter || !saisie) return;
    if (activeTicket) {
      toast.warning('Terminez d\'abord le ticket en cours.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await ticketService.callGlobalTicket(selectedCounter.id, saisie);
      if (response.success) {
        setActiveTicket(response.data);
        setNumeroGlobal('');
        setWaitingTickets((prev) => prev.filter((t) => t.ticketId !== response.data.ticketId));
        setWaitingCount((prev) => Math.max(0, prev - 1));
        await publierStatutGuichet('busy');
        if (socket && connected) socket.emit('forceDisplayUpdate');
      } else {
        setError(response.error || "Ce numéro n'a pas pu être appelé. Vérifiez-le sur le ticket du client.");
      }
    } catch (err) {
      // 404 (numéro inconnu) et 409 (ticket plus appelable) portent le message utile.
      const message = err.response?.data?.error || err.response?.data?.message;
      setError(message || "Ce numéro n'a pas pu être appelé. Vérifiez-le sur le ticket du client.");
    } finally {
      setLoading(false);
    }
  };

  // Terminer le service
  const completeService = async () => {
    if (!activeTicket) return;

    try {
      setLoading(true);
      const response = await ticketService.completeTicket(activeTicket.ticketId);

      if (response.success) {
        setActiveTicket(null);
        // Le guichet redevient disponible pour la supervision et l'écran.
        publierStatutGuichet('available');
        setError(null);
        // Compteurs relus chez le serveur — et non « +1 » avec la durée du dernier ticket
        // en guise de moyenne, ce que faisait la ligne précédente.
        chargerStatistiquesGuichet();
      } else {
        setError('Le ticket n\'a pas pu être clôturé : il reste en service. Réessayez.');
        console.error('Erreur:', response.error);
      }
    } catch (err) {
      console.error('Erreur lors de la fin du service:', err);
  console.error('Détails serveur:', err.response?.data || err.message);
  setError('Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.');

    } finally {
      setLoading(false);
    }
  };

  /**
   * TERMINER puis APPELER LE SUIVANT, d'un seul geste.
   *
   * C'est le geste le plus fréquent de la journée d'un guichetier, et le faire en deux
   * clics le fait oublier : le ticket reste « en service » alors que le client est parti,
   * l'écran affiche un guichet occupé, et la file ne bouge plus.
   *
   * Il ne réutilise PAS `completeService()` puis `callNextTicket()` : `setActiveTicket(null)`
   * n'a pas encore pris effet quand la seconde fonction s'exécute — sa garde `if (activeTicket)`
   * la ferait renoncer en silence. On enchaîne donc les deux appels de service ici, avec l'état
   * mis à jour une seule fois, à la fin.
   */
  const completeAndNext = async () => {
    if (!activeTicket || !selectedCounter) return;

    try {
      setLoading(true);
      setError(null);
      const fin = await ticketService.completeTicket(activeTicket.ticketId);
      if (!fin.success) {
        setError('Le ticket n\'a pas pu être clôturé : il reste en service. Réessayez.');
        return;
      }
      setActiveTicket(null);
      chargerStatistiquesGuichet();

      const suivant = await ticketService.callNextTicket(selectedCounter.id, selectedCounter.serviceType);
      // `suivant.data` vaut `null` quand la file est vide — depuis que la couche service
      // distingue le 204. Sans ce contrôle, la ligne suivante lisait `null.ticketId` et
      // l'exception retombait en « Erreur de connexion au serveur » : le geste le plus
      // courant de la journée se terminait par un bandeau rouge alors que tout allait bien.
      if (suivant?.success && suivant.data) {
        setActiveTicket(suivant.data);
        setWaitingTickets((prev) => prev.filter((t) => t.ticketId !== suivant.data.ticketId));
        setWaitingCount((prev) => Math.max(0, prev - 1));
        await publierStatutGuichet('busy');
      } else {
        // Le service vient bien d'être terminé : le guichet est libre, il n'y avait
        // simplement personne à appeler. Ce n'est pas une erreur, c'est une file vide.
        await publierStatutGuichet('available');
        toast.info('Service terminé — aucun client en attente.');
      }
      if (socket && connected) socket.emit('forceDisplayUpdate');
    } catch (err) {
      const message = err.response?.data?.error || err.response?.data?.message;
      // 404 = plus personne en attente : le ticket précédent est terminé, on le dit sans alarmer.
      if (err.response?.status === 404) {
        await publierStatutGuichet('available');
        toast.info('Service terminé — aucun client en attente.');
      } else {
        setError(message || 'Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * CHANGER DE GUICHET sans fermer la session du compte.
   *
   * À ne pas confondre avec la déconnexion du compte : un guichetier qui prend le guichet 3
   * en remplacement ne doit pas avoir à ressaisir ses identifiants. Le ticket en cours est
   * libéré côté serveur — l'abandonner « en service » le bloquerait pour tout le monde.
   */
  const changeCounter = async () => {
    if (activeTicket && !window.confirm(
      'Un ticket est en cours de service. Changer de guichet le laissera ouvert. Continuer ?',
    )) return;

    await publierStatutGuichet('available');
    if (socket && selectedCounter) socket.emit('counterLogout', { counterId: selectedCounter.id });

    setIsLoggedIn(false);
    setSelectedCounter(null);
    setActiveTicket(null);
    setWaitingTickets([]);
    setNumeroGlobal('');
    // Le bandeau d'erreur restait affiché après le changement de poste : un message de
    // panne d'il y a une minute continuait d'alarmer un guichetier sur un guichet sain.
    setError(null);
    setStats({ servedClients: 0, averageServiceTime: 0 });
  };

  // Rappeler un ticket
  const recallTicket = async () => {
    if (!activeTicket) return;

    try {
      setLoading(true);
      const response = await ticketService.recallTicket(activeTicket.ticketId);

      if (response.success) {
        // Émettre un événement WebSocket pour informer les écrans d'affichage
        if (socket && connected) {
          socket.emit('ticketRecalled', {
            ticketId: activeTicket.ticketId,
            counterId: selectedCounter.id
          });
        }
      } else {
        setError('Le rappel n\'a pas été diffusé. Le ticket reste en service — réessayez.');
      }
    } catch (err) {
      console.error('Erreur lors du rappel du ticket:', err);
      setError('Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.');
    } finally {
      setLoading(false);
    }
  };

  // Mettre en pause le service
  const pauseService = async () => {
    if (!activeTicket) return;

    try {
      setLoading(true);
      const response = await ticketService.pauseTicketService(activeTicket.ticketId);

      if (response.success) {
        // Mettre à jour le ticket actif avec le nouveau statut
        setActiveTicket(prev => ({
          ...prev,
          status: 'serving_paused'
        }));

        // Émettre un événement WebSocket
        if (socket && connected) {
          socket.emit('ticketServicePaused', {
            ticketId: activeTicket.ticketId,
            counterId: selectedCounter.id
          });
        }
      } else {
        setError('La mise en pause n\'a pas été enregistrée. Réessayez.');
      }
    } catch (err) {
      console.error('Erreur lors de la pause du service:', err);
      setError('Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.');
    } finally {
      setLoading(false);
    }
  };

  // Reprendre le service
  const resumeService = async () => {
    if (!activeTicket) return;

    try {
      setLoading(true);
      const response = await ticketService.resumeTicketService(activeTicket.ticketId);

      if (response.success) {
        // Mettre à jour le ticket actif avec le nouveau statut
        setActiveTicket(prev => ({
          ...prev,
          status: 'serving'
        }));

        // Émettre un événement WebSocket
        if (socket && connected) {
          socket.emit('ticketServiceResumed', {
            ticketId: activeTicket.ticketId,
            counterId: selectedCounter.id
          });
        }
      } else {
        setError('La reprise n\'a pas été enregistrée. Réessayez.');
      }
    } catch (err) {
      console.error('Erreur lors de la reprise du service:', err);
      setError('Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * RACCOURCIS CLAVIER du guichetier.
   *
   * Volontairement courts et limités aux gestes du métier. Le guichet Electron embarquait
   * une table générique de trente entrées dont la plupart ne faisaient qu'un `console.log` :
   * porter ce décor n'aurait rien apporté au guichetier, et aurait donné à croire que des
   * touches agissent alors qu'elles ne font rien.
   *
   * F2 appeler · F3 terminer · F4 terminer et appeler le suivant · F5 rappeler ·
   * F6 pause/reprise. On ne capte rien pendant une saisie (le champ « numéro d'ordre »
   * doit rester utilisable au clavier), ni pendant un appel en cours.
   */
  useEffect(() => {
    const auClavier = (e) => {
      const cible = e.target;
      const enSaisie = cible && (cible.tagName === 'INPUT' || cible.tagName === 'TEXTAREA' || cible.isContentEditable);
      if (enSaisie || loading || !isLoggedIn || !selectedCounter) return;

      const gestes = {
        F2: () => { if (!activeTicket && waitingCount > 0) callNextTicket(); },
        F3: () => { if (activeTicket) completeService(); },
        F4: () => { if (activeTicket) completeAndNext(); },
        F5: () => { if (activeTicket) recallTicket(); },
        F6: () => {
          if (!activeTicket) return;
          if (activeTicket.status === 'serving_paused') resumeService();
          else pauseService();
        },
      };

      const geste = gestes[e.key];
      if (!geste) return;
      e.preventDefault(); // F5 rechargerait la page en plein service
      geste();
    };

    window.addEventListener('keydown', auClavier);
    return () => window.removeEventListener('keydown', auClavier);
  });

  // (Gardes configLoading / configError déplacés APRÈS tous les hooks — règles des hooks React.)

  // Écran de connexion au guichet
  // // // // // // if (!isLoggedIn) {
  // // // // // //   return (
  // // // // // //     <div className="counter-container">
  // // // // // //       <div className="counter-select-container">
  // // // // // //         <div className="counter-select-header">
  // // // // // //           <h1 className="counter-select-title">
  // // // // // //             {config.agency?.name || "Agence"} - Connexion Guichet
  // // // // // //           </h1>
  // // // // // //           <p className="counter-select-company">{config.agency?.company || "Entreprise"}</p>
  // // // // // //         </div>
          
  // // // // // //         <div className="counter-select-content">
  // // // // // //           <h2 className="counter-select-label">Sélectionnez votre guichet:</h2>
            
  // // // // // //           <div className="counter-select-list">
  // // // // // //             {config.counters?.map(counter => {
  // // // // // //               const serviceType = config.serviceTypes.find(s => s.id === counter.serviceType);
  // // // // // //               return (
  // // // // // //                 <button
  // // // // // //                   key={counter.id}
  // // // // // //                   className="counter-select-item"
  // // // // // //                   onClick={() => loginToCounter(counter)}
  // // // // // //                 >
  // // // // // //                   <div className="counter-select-item-info">
  // // // // // //                     <span className="counter-select-item-name">{counter.name}</span>
  // // // // // //                     <span className="counter-select-item-service">
  // // // // // //                       ({serviceType?.name || counter.serviceType})
  // // // // // //                     </span>
  // // // // // //                   </div>
  // // // // // //                   <div 
  // // // // // //                     className="counter-select-service-dot" 
  // // // // // //                     style={{ backgroundColor: serviceType?.color || '#999' }}
  // // // // // //                   ></div>
  // // // // // //                 </button>
  // // // // // //               );
  // // // // // //             })}
  // // // // // //           </div>
  // // // // // //         </div>
  // // // // // //       </div>
  // // // // // //     </div>
  // // // // // //   );
  // // // // // // }

 // Fonction pour mettre à jour les données d'affichage
 const updateDisplayData = useCallback((data) => {
  if (!data) {
    console.log('Données de mise à jour nulles ou non définies');
    return;
  }

  console.log('Mise à jour des données d\'affichage reçue');

  if (data.activeTickets) {
    // Tickets en cours
    const serving = data.activeTickets.filter(ticket => ticket.status === 'serving');
    setServingTickets(serving);
    console.log(`Tickets en service mis à jour: ${serving.length}`);

    // Tickets en attente
    const waiting = data.activeTickets.filter(ticket => ticket.status === 'waiting');

    // ➕ Comptage total par serviceType
    const counts = {};
    waiting.forEach(ticket => {
      const type = ticket.serviceType;
      counts[type] = (counts[type] || 0) + 1;
    });

    setWaitingCounts(counts); // ← utilisé ailleurs dans ton composant d'affichage ?
    setTicketsByServiceType(counts); // ← c’est ici la mise à jour réelle pour CounterApp
    console.log('Tickets par service:', counts);
  } else {
    console.log('Pas de tickets actifs dans les données reçues');
  }

  setLastUpdateTime(new Date());
}, []);


   // Récupérer manuellement les tickets via l'API REST comme solution de secours
   const fetchTicketsManually = useCallback(async () => {
    try {
      console.log('Tentative de récupération manuelle des tickets via API REST');
      // `/api/display/state` et NON `/api/tickets/active` : cette dernière ne renvoie que
      // `serving` / `serving_paused` — jamais un ticket `waiting` (cf. le commentaire de
      // `queue/router.ts` sur cette route). Le filtre « en attente » ne trouvait donc
      // JAMAIS rien, et ce rafraîchissement — qui tourne toutes les 10 s — écrasait les
      // compteurs de la liste des guichets avec des zéros. Au retour à la liste, tous les
      // guichets annonçaient « 0 en attente » alors que des clients patientaient ; seul un
      // F5 les rétablissait, en rechargeant la page. Le même piège avait déjà été corrigé
      // pour /display : `/display/state` porte bien `waiting` ET `serving`.
      const response = await axios.get('/api/display/state');

      if (response.data.success) {
        const tickets = response.data.data?.activeTickets || [];
        console.log(`${tickets.length} tickets récupérés via API REST`);

        // Mettre à jour avec les mêmes fonctions que pour les données socket
                    updateDisplayData({ activeTickets: tickets, message: 'récupération manuelle via API' });
      } else {
        console.log('Échec de la récupération des tickets via API REST');
      }
    } catch (error) {
      console.error('Erreur lors de la récupération manuelle:', error);
    }
   }, [updateDisplayData]);
  // Demander manuellement une mise à jour des données
  const requestUpdate = useCallback(() => {
    if (socket && connected) {
      console.log('Demande de mise à jour des données via Socket.IO');
      socket.emit('requestDisplayUpdate');
    } else {
      console.log('Socket non connecté, tentative de récupération via API REST');
      fetchTicketsManually();
    }
  }, [socket, connected, fetchTicketsManually]);

  // Tient à jour ce que lisent les écouteurs temps réel. Placé ICI, après la définition de
  // `requestUpdate` : les écouteurs, eux, sont enregistrés une seule fois bien plus haut.
  useEffect(() => {
    selectedCounterRef.current = selectedCounter;
    activeTicketRef.current = activeTicket;
    // Seul `requestUpdate` (compteurs d'en-tête) passe encore par une ref : il est défini
    // plus bas dans le composant que les écouteurs.
    requestUpdateRef.current = requestUpdate;
  });

  // Ré-annonce du guichet choisi. L'enregistrement fait rejoindre la salle `counter-<id>`
  // côté serveur : il doit donc suivre le changement de poste. C'est un effet SÉPARÉ de
  // celui des écouteurs — le réenregistrement ne doit plus entraîner leur détachement.
  useEffect(() => {
    if (socket && connected && user && selectedCounter) {
      registerClient('counter', { counterId: selectedCounter.id, userId: user.id });
    }
  }, [socket, connected, user, selectedCounter, registerClient]);

  // Aperçu temps réel : charge à l'affichage puis rafraîchit toutes les 10 s (API REST, fiable).
  //
  // Il tourne AUSSI sur l'écran de sélection, et c'est le point important : les nombres
  // « en attente » qui aident à choisir un guichet doivent être vivants au moment du choix.
  // Auparavant la boucle ne tournait que pendant la session, si bien qu'au retour à la liste
  // plus rien ne rafraîchissait — les compteurs restaient figés sur leur dernière valeur.
  useEffect(() => {
    fetchTicketsManually();
    const id = setInterval(fetchTicketsManually, 10000);
    return () => clearInterval(id);
  }, [isLoggedIn, selectedCounter, fetchTicketsManually]);

  // ─── Gardes d'affichage : APRÈS tous les hooks (règles des hooks React) ───
  // Si la configuration est en cours de chargement
  if (configLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="p-8 bg-white rounded-lg shadow-md text-center">
          <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-700">Chargement de la configuration...</p>
        </div>
      </div>
    );
  }

  // Si une erreur s'est produite lors du chargement de la configuration
  if (configError || !config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="p-8 bg-white rounded-lg shadow-md text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-xl font-bold mb-4">Erreur</h1>
          <p className="mb-6 text-gray-700">{configError || "La configuration n'a pas pu être chargée"}</p>
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
            onClick={() => window.location.reload()}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }
// Vérifier si le guichet est occupé

   // Écran de connexion au guichet - mettre à jour pour utiliser les données de l'utilisateur
   if (!isLoggedIn) {
    return (
      <AuthCheck>
        <div className="counter-container">
          <div className="counter-select-container">
            <div className="counter-select-header">
              <h1 className="counter-select-title">
                {config.agency?.name || "Agence"} - Connexion Guichet
              </h1>
              <p className="counter-select-company">{config.agency?.company || "Entreprise"}</p>
            </div>
            
            <div className="counter-select-content">
              <div className="mb-4 bg-blue-50 p-4 rounded-lg">
                <p>Connecté en tant que: <strong>{user?.fullName}</strong> ({user?.role})</p>
              </div>
              
              <h2 className="counter-select-label">Sélectionnez votre guichet:</h2>
              
              <div className="counter-select-list">
                {config.counters?.map(counter => {
                  const serviceType = config.serviceTypes.find(s => s.id === counter.serviceType);
                  // GARDE-FOU : un guichet relié à un service inexistant ne peut appeler
                  // PERSONNE — le serveur cherche une file qui n'existe pas et répond
                  // « aucun ticket en attente » toute la journée, clients présents ou non.
                  // Mieux vaut le dire ici que de laisser un agent s'installer devant.
                  const serviceInconnu = !serviceType;
                  // Une SEULE source pour « en attente » : celle qui est rafraîchie. La liste
                  // et l'aperçu du bas affichaient deux nombres calculés séparément, qui
                  // finissaient par se contredire — exactement ce qu'un guichetier ne peut
                  // pas arbitrer.
                  const count = waitingCounts[counter.serviceType] ?? ticketsByServiceType[counter.serviceType] ?? 0;
                  const habituel = String(selectedCounter?.id) === String(counter.id);
                  return (
                    <button
                      key={counter.id}
                      className={`counter-select-item${habituel ? ' counter-select-item--habituel' : ''}`}
                      onClick={() => loginToCounter(counter)}
                      disabled={serviceInconnu}
                      title={serviceInconnu
                        ? `Ce guichet est relié au service « ${counter.serviceType} », qui n'existe pas dans la configuration. Corrigez-le dans la console : il ne peut appeler aucun client.`
                        : undefined}
                      style={serviceInconnu
                        ? { outline: '2px solid #b00020', opacity: 0.6, cursor: 'not-allowed' }
                        : (habituel ? { outline: '2px solid #2563eb' } : undefined)}
                    >
                      <div className="counter-select-item-info">
                        <span className="counter-select-item-name">
                          {counter.name}{habituel && !serviceInconnu ? ' — votre guichet habituel' : ''}
                        </span>
                        <span className="counter-select-item-service">
                          {serviceInconnu
                            ? `⚠ service « ${counter.serviceType} » inexistant — à corriger dans la console`
                            : `(${serviceType.name}) — ${count} en attente`}
                        </span>
                      </div>
                      <div 
                        className="counter-select-service-dot" 
                        style={{ backgroundColor: serviceType?.color || '#999' }}
                      ></div>
                    </button>
                  );
                })}
              </div>

              <button 
            className="mt-8 px-4 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition"
            onClick={requestUpdate}
          >
            Actualiser les données
          </button>

              <button
                onClick={() => {
                  // Passe par le CONTEXTE. Vider `localStorage` à la main laissait `AuthContext`
                  // persuadé que l'utilisateur était encore connecté : `isAuthenticated` restait
                  // vrai, `user` gardait ses données en mémoire, et l'en-tête `Authorization`
                  // d'axios n'était pas retiré. On « se déconnectait » sans se déconnecter.
                  // Même défaut que celui trouvé sur le login : deux chemins d'authentification,
                  // dont un qui ment à l'autre. Il ne doit y en avoir qu'un.
                  logoutCompte();
                  navigate('/login', { state: { from: '/counter' } });
                }}
                className="mt-6 p-2 text-red-500 underline"
              >
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </AuthCheck>
    );
  }

  // Interface principale du guichet
  const currentServiceType = config.serviceTypes.find(s => s.id === selectedCounter.serviceType);
  const counterStatus = activeTicket ? 'busy' : 'available';

 
  return (
    <AuthCheck>
    {/* ... [reste du code existant] */}

    <div className="counter-container">
      <div className="counter-card">
        {/* En-tête */}
        <div className="counter-header">
          <h1 className="counter-title">
            {selectedCounter.name} - {currentServiceType?.name || selectedCounter.serviceType}
          </h1>
          <p className="counter-status">
            Status: {counterStatus === 'available' ? 'Disponible' : 'Occupé'}
          </p>
        </div>

        {/* Aperçu temps réel — alimenté par updateDisplayData (socket / API REST) */}
        <div className="counter-live-status" style={{
          display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center',
          padding: '8px 16px', background: '#f1f5f9', borderTop: '1px solid #e2e8f0', fontSize: '13px'
        }}>
          <span title="Tickets en cours de service (tous guichets)">🟢 <strong>{servingTickets.length}</strong> en service</span>
          <span title="Total en attente, tous services">⏳ <strong>{Object.values(waitingCounts).reduce((a, b) => a + b, 0)}</strong> en attente</span>
          {Object.entries(waitingCounts).filter(([, n]) => n > 0).map(([type, n]) => (
            <span key={type} style={{ color: '#475569' }}>{type}: {n}</span>
          ))}
          <span style={{ marginLeft: 'auto', color: '#64748b' }}>
            🕒 {lastUpdateTime ? new Date(lastUpdateTime).toLocaleTimeString() : '—'}
          </span>
          <button
            onClick={requestUpdate}
            title="Rafraîchir les données"
            style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: '6px', padding: '2px 10px', cursor: 'pointer' }}
          >↻</button>
          <button
            onClick={() => setAideRaccourcis((v) => !v)}
            title="Raccourcis clavier"
            aria-expanded={aideRaccourcis}
            style={{
              border: '1px solid #cbd5e1', borderRadius: '6px', padding: '2px 10px', cursor: 'pointer',
              background: aideRaccourcis ? '#2563eb' : '#fff', color: aideRaccourcis ? '#fff' : 'inherit',
            }}
          >?</button>
        </div>

        {/* Rappel des raccourcis — replié par défaut, il ne prend la place de rien.
            Seuls les cinq gestes qui AGISSENT vraiment y figurent : annoncer une touche
            qui ne fait rien coûterait plus cher que de n'en annoncer aucune. */}
        {aideRaccourcis && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'center',
            padding: '8px 16px', background: '#eff6ff', borderTop: '1px solid #bfdbfe',
            fontSize: '13px', color: '#1e3a5f',
          }}>
            <strong style={{ color: '#1d4ed8' }}>Raccourcis</strong>
            {[
              ['F2', 'Appeler prochain'],
              ['F3', 'Terminer'],
              ['F4', 'Terminer + Suivant'],
              ['F5', 'Rappeler'],
              ['F6', 'Pause / Reprise'],
            ].map(([touche, libelle]) => (
              <span key={touche}>
                <kbd style={{
                  background: '#fff', border: '1px solid #93c5fd', borderRadius: '4px',
                  padding: '1px 6px', fontFamily: 'monospace', fontWeight: 'bold', marginRight: '5px',
                }}>{touche}</kbd>
                {libelle}
              </span>
            ))}
            <span style={{ marginLeft: 'auto', color: '#64748b' }}>
              inactifs pendant une saisie
            </span>
          </div>
        )}

        <div className="counter-content">
          {/* Message d'erreur */}
          {error && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded shadow">
              <div className="flex">
                <div>
                  <p className="font-bold">Erreur</p>
                  <p>{error}</p>
                </div>
              </div>
              <button 
                className="mt-2 text-sm underline"
                onClick={() => setError(null)}
              >
                Fermer
              </button>
            </div>
          )}
          
          {/* Ticket actuel */}
          <div className="current-ticket-section">
            <div className="current-ticket-label">Ticket actuel:</div>
            
            {activeTicket ? (
              <div className="current-ticket-info">
                {/* Le libellé du SERVEUR — la même chaîne exactement que l'écran affiche,
                    que la voix annonce et que le papier porte. */}
                <div className="current-ticket-number">
                  {libelleTicket(activeTicket)}
                </div>
                {activeTicket.globalNumber ? (
                  <div className="muted" style={{ fontSize: '12px', opacity: 0.7 }}>
                    n° d'ordre {activeTicket.globalNumber}
                  </div>
                ) : null}
                {activeTicket.status === 'serving_paused' && (
                  <div className="ticket-status-badge" style={{
                    backgroundColor: '#ef4444',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    marginTop: '8px'
                  }}>
                    En Pause
                  </div>
                )}
              </div>
            ) : (
              <div className="no-ticket-message">
                Aucun ticket en service
              </div>
            )}
          </div>
          
          {/* File d'attente */}
          <div className="queue-section">
            <div className="queue-header">
              <div className="queue-label">File d'attente {currentServiceType?.name || selectedCounter.serviceType}</div>
              <div className="queue-count">
                {waitingCount} en attente
                {fileRafraichieA && (
                  <span style={{ display: 'block', fontSize: '11px', color: '#64748b', fontWeight: 'normal' }}>
                    à jour à {fileRafraichieA.toLocaleTimeString()}
                  </span>
                )}
              </div>
            </div>
            
            {waitingCount > 0 && (
              <div className="next-ticket-info">
                Prochain: {waitingTickets.length > 0 ? libelleTicket(waitingTickets[0]) : '-'}
              </div>
            )}

            {/* APPEL PAR NUMÉRO D'ORDRE — le client qui revient après son tour. Sans cela,
                le guichetier n'a d'autre choix que de vider la file devant lui. */}
            <div className="call-global" style={{ display: 'flex', gap: '8px', margin: '10px 0' }}>
              <input
                type="number"
                min="1"
                inputMode="numeric"
                placeholder="n° d'ordre du ticket"
                value={numeroGlobal}
                onChange={(e) => setNumeroGlobal(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') callGlobalTicket(); }}
                disabled={!!activeTicket || loading}
                style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
              />
              <button
                onClick={callGlobalTicket}
                disabled={!!activeTicket || loading || !String(numeroGlobal).trim()}
                className={`action-button ${(!!activeTicket || loading || !String(numeroGlobal).trim()) ? 'disabled' : ''}`}
                title="Appeler ce ticket précis (client dont le tour est passé)"
              >
                Appeler ce n°
              </button>
            </div>

            <div className="action-buttons">
              <button
                onClick={callNextTicket}
                disabled={!!activeTicket || waitingCount === 0 || loading}
                className={`action-button call-next-button ${(!!activeTicket || waitingCount === 0 || loading) ? 'disabled' : ''}`}
              >
                {loading ? 'Chargement...' : 'Appeler Prochain'}
              </button>

              <button
                onClick={completeService}
                disabled={!activeTicket || loading}
                className={`action-button complete-button ${(!activeTicket || loading) ? 'disabled' : ''}`}
                title="Terminer le service (F3)"
              >
                {loading ? 'Chargement...' : 'Terminer Service'}
              </button>

              {/* Le geste le plus fréquent de la journée : un seul clic, pas deux. */}
              <button
                onClick={completeAndNext}
                disabled={!activeTicket || loading}
                className={`action-button complete-next-button ${(!activeTicket || loading) ? 'disabled' : ''}`}
                style={{ backgroundColor: '#2563eb', color: 'white' }}
                title="Terminer et appeler le suivant (F4)"
              >
                {loading ? 'Chargement...' : 'Terminer + Suivant'}
              </button>

              {/* Boutons RAPPEL et PAUSE/REPRISE */}
              {activeTicket && (
                <>
                  <button
                    onClick={recallTicket}
                    disabled={loading}
                    className={`action-button recall-button ${loading ? 'disabled' : ''}`}
                    style={{ backgroundColor: '#f59e0b', color: 'white' }}
                  >
                    {loading ? 'Chargement...' : 'Rappeler'}
                  </button>

                  {activeTicket.status === 'serving_paused' ? (
                    <button
                      onClick={resumeService}
                      disabled={loading}
                      className={`action-button resume-button ${loading ? 'disabled' : ''}`}
                      style={{ backgroundColor: '#10b981', color: 'white' }}
                    >
                      {loading ? 'Chargement...' : 'Reprendre'}
                    </button>
                  ) : (
                    <button
                      onClick={pauseService}
                      disabled={loading}
                      className={`action-button pause-button ${loading ? 'disabled' : ''}`}
                      style={{ backgroundColor: '#ef4444', color: 'white' }}
                    >
                      {loading ? 'Chargement...' : 'Pause'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          
          {/* Statistiques */}
          <div className="counter-stats">
            <div className="counter-stat-card">
              <div className="counter-stat-label">Clients servis</div>
              <div className="counter-stat-value">{stats.servedClients}</div>
            </div>
            
            <div className="counter-stat-card">
              <div className="counter-stat-label">Temps moyen</div>
              <div className="counter-stat-value">
                {stats.averageServiceTime > 0 
                  ? `${Math.round(stats.averageServiceTime / 60)} min` 
                  : '-'}
              </div>
            </div>
          </div>
        </div>
        
        {/* Pied de page */}
        <div className="counter-footer">
          <div className={`counter-status-indicator counter-status-${counterStatus}`}>
            <div className="status-dot"></div>
            <span className="status-text">En ligne</span>
          </div>
          
          {/* Deux gestes DISTINCTS, qui étaient réduits à un bouton « + » :
              — changer de guichet garde la session du compte (remplacement en cours de journée) ;
              — quitter le compte ferme la session (fin de poste, poste partagé). */}
          <div className="counter-actions" style={{ display: 'flex', gap: '8px' }}>
            <button
              className="counter-action-button"
              onClick={changeCounter}
              title="Changer de guichet (garde votre session)"
            >
              Changer de guichet
            </button>
            <button
              className="counter-action-button"
              onClick={() => { logout(); logoutCompte(); }}
              title="Quitter le compte"
            >
              Quitter
            </button>
          </div>
        </div>
      </div>
    </div>
    </AuthCheck>
  );
};

export default CounterApp;

// // // // // // import React, { useState, useEffect } from 'react';
// // // // // // import { useConfig } from '../../contexts/ConfigContext';
// // // // // // import { useSocket } from '../../contexts/SocketContext';
// // // // // // import ticketService from '../../services/ticketService';
// // // // // // import counterServiceAPI from '../../services/counterService'; // Renommé pour éviter le conflit

// // // // // // /**
// // // // // //  * Application pour les guichetiers
// // // // // //  * Permet d'appeler les clients et de gérer le service
// // // // // //  */
// // // // // // const CounterApp = () => {
// // // // // //   const { config, loading: configLoading, error: configError } = useConfig();
// // // // // //   const { socket, registerClient } = useSocket();
  
// // // // // //   // États
// // // // // //   const [isLoggedIn, setIsLoggedIn] = useState(false);
// // // // // //   const [selectedCounter, setSelectedCounter] = useState(null);
// // // // // //   const [activeTicket, setActiveTicket] = useState(null);
// // // // // //   const [waitingTickets, setWaitingTickets] = useState([]);
// // // // // //   const [loading, setLoading] = useState(false);
// // // // // //   const [error, setError] = useState(null);
// // // // // //   const [stats, setStats] = useState({
// // // // // //     servedClients: 0,
// // // // // //     averageServiceTime: 0
// // // // // //   });
// // // // // //   const [waitingCount, setWaitingCount] = useState(0);

// // // // // //   // Enregistrer comme client "counter" au montage
// // // // // //   useEffect(() => {
// // // // // //     if (socket) {
// // // // // //       registerClient('counter');
      
// // // // // //       // Écouter les mises à jour des tickets
// // // // // //       socket.on('ticketCancelled', (ticket) => {
// // // // // //         // Si c'est le ticket actif, le supprimer
// // // // // //         if (activeTicket && ticket.ticketId === activeTicket.ticketId) {
// // // // // //           setActiveTicket(null);
// // // // // //         }
        
// // // // // //         // Mettre à jour la liste des tickets en attente
// // // // // //         setWaitingTickets(prev => prev.filter(t => t.ticketId !== ticket.ticketId));
// // // // // //       });
      
// // // // // //       // Écouter les nouveaux tickets
// // // // // //       socket.on('newTicket', (ticket) => {
// // // // // //         // Ajouter à la liste des tickets en attente s'il correspond au service du guichet
// // // // // //         if (selectedCounter && ticket.serviceType === selectedCounter.serviceType) {
// // // // // //           setWaitingTickets(prev => [...prev, ticket]);
// // // // // //           setWaitingCount(prev => prev + 1);
// // // // // //         }
// // // // // //       });
// // // // // //     }
    
// // // // // //     return () => {
// // // // // //       if (socket) {
// // // // // //         socket.off('ticketCancelled');
// // // // // //         socket.off('newTicket');
// // // // // //       }
// // // // // //     };
// // // // // //   }, [socket, registerClient, activeTicket, selectedCounter]);

// // // // // //   // Charger les tickets en attente lors de la connexion à un guichet
// // // // // //   useEffect(() => {
// // // // // //     if (selectedCounter && isLoggedIn) {
// // // // // //       loadWaitingTickets();
// // // // // //       loadCounterStatus();
      
// // // // // //       // Enregistrer le client avec l'ID du guichet
// // // // // //       if (socket) {
// // // // // //         socket.emit('counterLogin', { counterId: selectedCounter.id });
// // // // // //       }
// // // // // //     }
// // // // // //   }, [selectedCounter, isLoggedIn]);

// // // // // //   // Charger les tickets en attente pour le service correspondant au guichet
// // // // // //   const loadWaitingTickets = async () => {
// // // // // //     if (!selectedCounter) return;
    
// // // // // //     try {
// // // // // //       setLoading(true);
// // // // // //       const response = await ticketService.getWaitingTickets(selectedCounter.serviceType);
      
// // // // // //       if (response.success) {
// // // // // //         setWaitingTickets(response.data);
// // // // // //         setWaitingCount(response.data.length);
// // // // // //       } else {
// // // // // //         setError('La file n\'a pas pu être chargée. Elle se rafraîchira toute seule au prochain mouvement.');
// // // // // //       }
// // // // // //     } catch (err) {
// // // // // //       console.error('Erreur lors du chargement des tickets:', err);
// // // // // //       setError('Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.');
// // // // // //     } finally {
// // // // // //       setLoading(false);
// // // // // //     }
// // // // // //   };

// // // // // //   // Charger le statut actuel du guichet
// // // // // //   const loadCounterStatus = async () => {
// // // // // //     if (!selectedCounter) return;
    
// // // // // //     try {
// // // // // //       setLoading(true);
// // // // // //       const response = await counterServiceAPI.getCounterStatus(selectedCounter.id);
      
// // // // // //       if (response.success) {
// // // // // //         // Mettre à jour les statistiques
// // // // // //         setStats({
// // // // // //           servedClients: response.data.dailyStats?.servedClients || 0,
// // // // // //           averageServiceTime: response.data.dailyStats?.averageServiceTime || 0
// // // // // //         });
        
// // // // // //         // Si le guichet a un ticket en cours, le récupérer
// // // // // //         if (response.data.currentTicket) {
// // // // // //           setActiveTicket(response.data.currentTicket);
// // // // // //         }
// // // // // //       } else {
// // // // // //         setError('L\'état du guichet n\'a pas pu être lu. Le service reste possible.');
// // // // // //       }
// // // // // //     } catch (err) {
// // // // // //       console.error('Erreur lors du chargement du statut du guichet:', err);
// // // // // //       setError('Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.');
// // // // // //     } finally {
// // // // // //       setLoading(false);
// // // // // //     }
// // // // // //   };

// // // // // //   // Se connecter à un guichet
// // // // // //   const loginToCounter = (counter) => {
// // // // // //     setSelectedCounter(counter);
// // // // // //     setIsLoggedIn(true);
// // // // // //   };

// // // // // //   // Se déconnecter du guichet
// // // // // //   const logout = () => {
// // // // // //     if (activeTicket) {
// // // // // //       if (!window.confirm("Vous avez un ticket actif. Voulez-vous vraiment vous déconnecter?")) {
// // // // // //         return;
// // // // // //       }
// // // // // //     }
    
// // // // // //     // Déconnexion du WebSocket
// // // // // //     if (socket && selectedCounter) {
// // // // // //       socket.emit('counterLogout', { counterId: selectedCounter.id });
// // // // // //     }
    
// // // // // //     setIsLoggedIn(false);
// // // // // //     setSelectedCounter(null);
// // // // // //     setActiveTicket(null);
// // // // // //     setWaitingTickets([]);
// // // // // //   };

// // // // // //   // Appeler le prochain ticket
// // // // // //   const callNextTicket = async () => {
// // // // // //     if (!selectedCounter || activeTicket) return;
    
// // // // // //     try {
// // // // // //       setLoading(true);
// // // // // //       const response = await ticketService.callNextTicket(
// // // // // //         selectedCounter.id, 
// // // // // //         selectedCounter.serviceType
// // // // // //       );
      
// // // // // //       if (response.success) {
// // // // // //         setActiveTicket(response.data);
// // // // // //         // Retirer ce ticket de la liste d'attente
// // // // // //         setWaitingTickets(prev => prev.filter(t => t.ticketId !== response.data.ticketId));
// // // // // //         setWaitingCount(prev => prev - 1);
        
// // // // // //           // AJOUT: Déclencher une mise à jour immédiate pour tous les écrans via Socket.io
// // // // // //       if (socket && connected) {
// // // // // //         socket.emit('forceDisplayUpdate');
// // // // // //       }

// // // // // //       } else {
// // // // // //         setError(response.error || 'Erreur lors de l\'appel du prochain ticket');
// // // // // //       }
// // // // // //     } catch (err) {
// // // // // //       console.error('Erreur lors de l\'appel du prochain ticket:', err);
// // // // // //       if (err.response && err.response.status === 404) {
// // // // // //         setError('Aucun ticket en attente pour ce service');
// // // // // //       } else {
// // // // // //         setError('Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.');
// // // // // //       }
// // // // // //     } finally {
// // // // // //       setLoading(false);
// // // // // //     }
// // // // // //   };

// // // // // //   // Terminer le service
// // // // // //   const completeService = async () => {
// // // // // //     if (!activeTicket) return;
    
// // // // // //     try {
// // // // // //       setLoading(true);
// // // // // //       const response = await ticketService.completeTicket(activeTicket.ticketId);
      
// // // // // //       if (response.success) {
// // // // // //         setActiveTicket(null);
// // // // // //         // Mettre à jour les statistiques
// // // // // //         setStats(prev => ({
// // // // // //           servedClients: prev.servedClients + 1,
// // // // // //           averageServiceTime: response.data.serviceTime // Simplifié pour l'exemple
// // // // // //         }));
// // // // // //       } else {
// // // // // //         setError('Le ticket n\'a pas pu être clôturé : il reste en service. Réessayez.');
// // // // // //       }
// // // // // //     } catch (err) {
// // // // // //       console.error('Erreur lors de la fin du service:', err);
// // // // // //       setError('Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.');
// // // // // //     } finally {
// // // // // //       setLoading(false);
// // // // // //     }
// // // // // //   };

// // // // // //   // Si la configuration est en cours de chargement
// // // // // //   if (configLoading) {
// // // // // //     return (
// // // // // //       <div className="min-h-screen flex items-center justify-center bg-gray-100">
// // // // // //         <div className="p-8 bg-white rounded-lg shadow-md text-center">
// // // // // //           <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
// // // // // //           <p className="text-gray-700">Chargement de la configuration...</p>
// // // // // //         </div>
// // // // // //       </div>
// // // // // //     );
// // // // // //   }

// // // // // //   // Si une erreur s'est produite lors du chargement de la configuration
// // // // // //   if (configError || !config) {
// // // // // //     return (
// // // // // //       <div className="min-h-screen flex items-center justify-center bg-gray-100">
// // // // // //         <div className="p-8 bg-white rounded-lg shadow-md text-center max-w-md">
// // // // // //           <div className="text-red-500 text-6xl mb-4">⚠️</div>
// // // // // //           <h1 className="text-xl font-bold mb-4">Erreur</h1>
// // // // // //           <p className="mb-6 text-gray-700">{configError || "La configuration n'a pas pu être chargée"}</p>
// // // // // //           <button 
// // // // // //             className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
// // // // // //             onClick={() => window.location.reload()}
// // // // // //           >
// // // // // //             Réessayer
// // // // // //           </button>
// // // // // //         </div>
// // // // // //       </div>
// // // // // //     );
// // // // // //   }

// // // // // //   // Écran de connexion au guichet
// // // // // //   if (!isLoggedIn) {
// // // // // //     return (
// // // // // //       <div className="counter-container">
// // // // // //         <div className="counter-select-container">
// // // // // //           <div className="counter-select-header">
// // // // // //             <h1 className="counter-select-title">
// // // // // //               {config.agency?.name || "Agence"} - Connexion Guichet
// // // // // //             </h1>
// // // // // //             <p className="counter-select-company">{config.agency?.company || "Entreprise"}</p>
// // // // // //           </div>
          
// // // // // //           <div className="counter-select-content">
// // // // // //             <h2 className="counter-select-label">Sélectionnez votre guichet:</h2>
            
// // // // // //             <div className="counter-select-list">
// // // // // //               {config.counters?.map(counter => {
// // // // // //                 const serviceType = config.serviceTypes.find(s => s.id === counter.serviceType);
// // // // // //                 return (
// // // // // //                   <button
// // // // // //                     key={counter.id}
// // // // // //                     className="counter-select-item"
// // // // // //                     onClick={() => loginToCounter(counter)}
// // // // // //                   >
// // // // // //                     <div className="counter-select-item-info">
// // // // // //                       <span className="counter-select-item-name">{counter.name}</span>
// // // // // //                       <span className="counter-select-item-service">
// // // // // //                         ({serviceType?.name || counter.serviceType})
// // // // // //                       </span>
// // // // // //                     </div>
// // // // // //                     <div 
// // // // // //                       className="counter-select-service-dot" 
// // // // // //                       style={{ backgroundColor: serviceType?.color || '#999' }}
// // // // // //                     ></div>
// // // // // //                   </button>
// // // // // //                 );
// // // // // //               })}
// // // // // //             </div>
// // // // // //           </div>
// // // // // //         </div>
// // // // // //       </div>
// // // // // //     );
// // // // // //   }

// // // // // //   // Interface principale du guichet
// // // // // //   const currentServiceType = config.serviceTypes.find(s => s.id === selectedCounter.serviceType);
// // // // // //   const counterStatus = activeTicket ? 'busy' : 'available';

// // // // // //   return (
// // // // // //     <div className="counter-container">
// // // // // //       <div className="counter-card">
// // // // // //         {/* En-tête */}
// // // // // //         <div className="counter-header">
// // // // // //           <h1 className="counter-title">
// // // // // //             {selectedCounter.name} - {currentServiceType?.name || selectedCounter.serviceType}
// // // // // //           </h1>
// // // // // //           <p className="counter-status">
// // // // // //             Status: {counterStatus === 'available' ? 'Disponible' : 'Occupé'}
// // // // // //           </p>
// // // // // //         </div>
        
// // // // // //         <div className="counter-content">
// // // // // //           {/* Message d'erreur */}
// // // // // //           {error && (
// // // // // //             <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded shadow">
// // // // // //               <div className="flex">
// // // // // //                 <div>
// // // // // //                   <p className="font-bold">Erreur</p>
// // // // // //                   <p>{error}</p>
// // // // // //                 </div>
// // // // // //               </div>
// // // // // //               <button 
// // // // // //                 className="mt-2 text-sm underline"
// // // // // //                 onClick={() => setError(null)}
// // // // // //               >
// // // // // //                 Fermer
// // // // // //               </button>
// // // // // //             </div>
// // // // // //           )}
          
// // // // // //           {/* Ticket actuel */}
// // // // // //           <div className="current-ticket-section">
// // // // // //             <div className="current-ticket-label">Ticket actuel:</div>
            
// // // // // //             {activeTicket ? (
// // // // // //               <div className="current-ticket-number">
// // // // // //                 {activeTicket.ticketId}
// // // // // //               </div>
// // // // // //             ) : (
// // // // // //               <div className="no-ticket-message">
// // // // // //                 Aucun ticket en service
// // // // // //               </div>
// // // // // //             )}
// // // // // //           </div>
          
// // // // // //           {/* File d'attente */}
// // // // // //           <div className="queue-section">
// // // // // //             <div className="queue-header">
// // // // // //               <div className="queue-label">File d'attente {currentServiceType?.name || selectedCounter.serviceType}</div>
// // // // // //               <div className="queue-count">{waitingCount} en attente</div>
// // // // // //             </div>
            
// // // // // //             {waitingCount > 0 && (
// // // // // //               <div className="next-ticket-info">
// // // // // //                 Prochain: {waitingTickets.length > 0 ? waitingTickets[0]?.ticketId : '-'}
// // // // // //               </div>
// // // // // //             )}
            
// // // // // //             <div className="action-buttons">
// // // // // //               <button 
// // // // // //                 onClick={callNextTicket}
// // // // // //                 disabled={!!activeTicket || waitingCount === 0 || loading}
// // // // // //                 className={`action-button call-next-button ${(!!activeTicket || waitingCount === 0 || loading) ? 'disabled' : ''}`}
// // // // // //               >
// // // // // //                 {loading ? 'Chargement...' : 'Appeler Prochain'}
// // // // // //               </button>
              
// // // // // //               <button 
// // // // // //                 onClick={completeService}
// // // // // //                 disabled={!activeTicket || loading}
// // // // // //                 className={`action-button complete-button ${(!activeTicket || loading) ? 'disabled' : ''}`}
// // // // // //               >
// // // // // //                 {loading ? 'Chargement...' : 'Terminer Service'}
// // // // // //               </button>
// // // // // //             </div>
// // // // // //           </div>
          
// // // // // //           {/* Statistiques */}
// // // // // //           <div className="counter-stats">
// // // // // //             <div className="counter-stat-card">
// // // // // //               <div className="counter-stat-label">Clients servis</div>
// // // // // //               <div className="counter-stat-value">{stats.servedClients}</div>
// // // // // //             </div>
            
// // // // // //             <div className="counter-stat-card">
// // // // // //               <div className="counter-stat-label">Temps moyen</div>
// // // // // //               <div className="counter-stat-value">
// // // // // //                 {stats.averageServiceTime > 0 
// // // // // //                   ? `${Math.round(stats.averageServiceTime / 60)} min` 
// // // // // //                   : '-'}
// // // // // //               </div>
// // // // // //             </div>
// // // // // //           </div>
// // // // // //         </div>
        
// // // // // //         {/* Pied de page */}
// // // // // //         <div className="counter-footer">
// // // // // //           <div className={`counter-status-indicator counter-status-${counterStatus}`}>
// // // // // //             <div className="status-dot"></div>
// // // // // //             <span className="status-text">En ligne</span>
// // // // // //           </div>
          
// // // // // //           <div className="counter-actions">
// // // // // //             <button
// // // // // //               className="counter-action-button"
// // // // // //               onClick={logout}
// // // // // //               title="Déconnexion"
// // // // // //             >
// // // // // //               +
// // // // // //             </button>
// // // // // //           </div>
// // // // // //         </div>
// // // // // //       </div>
// // // // // //     </div>
// // // // // //   );
// // // // // // };

// // // // // // export default CounterApp;



// // // // // // // // // // // import React, { useState, useEffect } from 'react';
// // // // // // // // // // // import { useConfig } from '../../contexts/ConfigContext';
// // // // // // // // // // // import { useSocket } from '../../contexts/SocketContext';
// // // // // // // // // // // import ticketService from '../../services/ticketService';
// // // // // // // // // // // import counterServiceAPI  from '../../services/counterService';

// // // // // // // // // // // /**
// // // // // // // // // // //  * Application pour les guichetiers
// // // // // // // // // // //  * Permet d'appeler les clients et de gérer le service
// // // // // // // // // // //  */
// // // // // // // // // // // const CounterApp = () => {
// // // // // // // // // // //   const { config, loading: configLoading, error: configError } = useConfig();
// // // // // // // // // // //   const { socket, registerClient } = useSocket();
  
// // // // // // // // // // //   // États
// // // // // // // // // // //   const [isLoggedIn, setIsLoggedIn] = useState(false);
// // // // // // // // // // //   const [selectedCounter, setSelectedCounter] = useState(null);
// // // // // // // // // // //   const [activeTicket, setActiveTicket] = useState(null);
// // // // // // // // // // //   const [waitingTickets, setWaitingTickets] = useState([]);
// // // // // // // // // // //   const [loading, setLoading] = useState(false);
// // // // // // // // // // //   const [error, setError] = useState(null);
// // // // // // // // // // //   const [stats, setStats] = useState({
// // // // // // // // // // //     servedClients: 0,
// // // // // // // // // // //     averageServiceTime: 0
// // // // // // // // // // //   });
// // // // // // // // // // //   const [waitingCount, setWaitingCount] = useState(0);

// // // // // // // // // // //   // Enregistrer comme client "counter" au montage
// // // // // // // // // // //   useEffect(() => {
// // // // // // // // // // //     if (socket) {
// // // // // // // // // // //       registerClient('counter');
      
// // // // // // // // // // //       // Écouter les mises à jour des tickets
// // // // // // // // // // //       socket.on('ticketCancelled', (ticket) => {
// // // // // // // // // // //         // Si c'est le ticket actif, le supprimer
// // // // // // // // // // //         if (activeTicket && ticket.ticketId === activeTicket.ticketId) {
// // // // // // // // // // //           setActiveTicket(null);
// // // // // // // // // // //         }
        
// // // // // // // // // // //         // Mettre à jour la liste des tickets en attente
// // // // // // // // // // //         setWaitingTickets(prev => prev.filter(t => t.ticketId !== ticket.ticketId));
// // // // // // // // // // //       });
      
// // // // // // // // // // //       // Écouter les nouveaux tickets
// // // // // // // // // // //       socket.on('newTicket', (ticket) => {
// // // // // // // // // // //         // Ajouter à la liste des tickets en attente s'il correspond au service du guichet
// // // // // // // // // // //         if (selectedCounter && ticket.serviceType === selectedCounter.serviceType) {
// // // // // // // // // // //           setWaitingTickets(prev => [...prev, ticket]);
// // // // // // // // // // //         }
// // // // // // // // // // //       });
// // // // // // // // // // //     }
    
// // // // // // // // // // //     return () => {
// // // // // // // // // // //       if (socket) {
// // // // // // // // // // //         socket.off('ticketCancelled');
// // // // // // // // // // //         socket.off('newTicket');
// // // // // // // // // // //       }
// // // // // // // // // // //     };
// // // // // // // // // // //   }, [socket, registerClient, activeTicket, selectedCounter]);

// // // // // // // // // // //   // Charger les tickets en attente lors de la connexion à un guichet
// // // // // // // // // // //   useEffect(() => {
// // // // // // // // // // //     if (selectedCounter && isLoggedIn) {
// // // // // // // // // // //       loadWaitingTickets();
// // // // // // // // // // //       loadCounterStatus();
      
// // // // // // // // // // //       // Enregistrer le client avec l'ID du guichet
// // // // // // // // // // //       if (socket) {
// // // // // // // // // // //         socket.emit('counterLogin', { counterId: selectedCounter.id });
// // // // // // // // // // //       }
// // // // // // // // // // //     }
// // // // // // // // // // //   }, [selectedCounter, isLoggedIn]);

// // // // // // // // // // //   // Charger les tickets en attente pour le service correspondant au guichet
// // // // // // // // // // //   const loadWaitingTickets = async () => {
// // // // // // // // // // //     if (!selectedCounter) return;
    
// // // // // // // // // // //     try {
// // // // // // // // // // //       setLoading(true);
// // // // // // // // // // //       const response = await ticketService.getWaitingTickets(selectedCounter.serviceType);
      
// // // // // // // // // // //       if (response.success) {
// // // // // // // // // // //         setWaitingTickets(response.data);
// // // // // // // // // // //         setWaitingCount(response.data.length);
// // // // // // // // // // //       } else {
// // // // // // // // // // //         setError('La file n\'a pas pu être chargée. Elle se rafraîchira toute seule au prochain mouvement.');
// // // // // // // // // // //       }
// // // // // // // // // // //     } catch (err) {
// // // // // // // // // // //       console.error('Erreur lors du chargement des tickets:', err);
// // // // // // // // // // //       setError('Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.');
// // // // // // // // // // //     } finally {
// // // // // // // // // // //       setLoading(false);
// // // // // // // // // // //     }
// // // // // // // // // // //   };

// // // // // // // // // // //   // Charger le statut actuel du guichet
// // // // // // // // // // //   const loadCounterStatus = async () => {
// // // // // // // // // // //     if (!selectedCounter) return;
    
// // // // // // // // // // //     try {
// // // // // // // // // // //       setLoading(true);
// // // // // // // // // // //       const response = await counterServiceAPI .getCounterStatus(selectedCounter.id);
      
// // // // // // // // // // //       if (response.success) {
// // // // // // // // // // //         // Mettre à jour les statistiques
// // // // // // // // // // //         setStats({
// // // // // // // // // // //           servedClients: response.data.dailyStats?.servedClients || 0,
// // // // // // // // // // //           averageServiceTime: response.data.dailyStats?.averageServiceTime || 0
// // // // // // // // // // //         });
        
// // // // // // // // // // //         // Si le guichet a un ticket en cours, le récupérer
// // // // // // // // // // //         if (response.data.currentTicket) {
// // // // // // // // // // //           setActiveTicket(response.data.currentTicket);
// // // // // // // // // // //         }
// // // // // // // // // // //       } else {
// // // // // // // // // // //         setError('L\'état du guichet n\'a pas pu être lu. Le service reste possible.');
// // // // // // // // // // //       }
// // // // // // // // // // //     } catch (err) {
// // // // // // // // // // //       console.error('Erreur lors du chargement du statut du guichet:', err);
// // // // // // // // // // //       setError('Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.');
// // // // // // // // // // //     } finally {
// // // // // // // // // // //       setLoading(false);
// // // // // // // // // // //     }
// // // // // // // // // // //   };

// // // // // // // // // // //   // Se connecter à un guichet
// // // // // // // // // // //   const loginToCounter = (counter) => {
// // // // // // // // // // //     setSelectedCounter(counter);
// // // // // // // // // // //     setIsLoggedIn(true);
// // // // // // // // // // //   };

// // // // // // // // // // //   // Se déconnecter du guichet
// // // // // // // // // // //   const logout = () => {
// // // // // // // // // // //     if (activeTicket) {
// // // // // // // // // // //       if (!window.confirm("Vous avez un ticket actif. Voulez-vous vraiment vous déconnecter?")) {
// // // // // // // // // // //         return;
// // // // // // // // // // //       }
// // // // // // // // // // //     }
    
// // // // // // // // // // //     // Déconnexion du WebSocket
// // // // // // // // // // //     if (socket && selectedCounter) {
// // // // // // // // // // //       socket.emit('counterLogout', { counterId: selectedCounter.id });
// // // // // // // // // // //     }
    
// // // // // // // // // // //     setIsLoggedIn(false);
// // // // // // // // // // //     setSelectedCounter(null);
// // // // // // // // // // //     setActiveTicket(null);
// // // // // // // // // // //     setWaitingTickets([]);
// // // // // // // // // // //   };

// // // // // // // // // // //   // Appeler le prochain ticket
// // // // // // // // // // //   const callNextTicket = async () => {
// // // // // // // // // // //     if (!selectedCounter || activeTicket) return;
    
// // // // // // // // // // //     try {
// // // // // // // // // // //       setLoading(true);
// // // // // // // // // // //       const response = await ticketService.callNextTicket(
// // // // // // // // // // //         selectedCounter.id, 
// // // // // // // // // // //         selectedCounter.serviceType
// // // // // // // // // // //       );
      
// // // // // // // // // // //       if (response.success) {
// // // // // // // // // // //         setActiveTicket(response.data);
// // // // // // // // // // //         // Retirer ce ticket de la liste d'attente
// // // // // // // // // // //         setWaitingTickets(prev => prev.filter(t => t.ticketId !== response.data.ticketId));
// // // // // // // // // // //         setWaitingCount(prev => prev - 1);
// // // // // // // // // // //       } else {
// // // // // // // // // // //         setError(response.error || 'Erreur lors de l\'appel du prochain ticket');
// // // // // // // // // // //       }
// // // // // // // // // // //     } catch (err) {
// // // // // // // // // // //       console.error('Erreur lors de l\'appel du prochain ticket:', err);
// // // // // // // // // // //       if (err.response && err.response.status === 404) {
// // // // // // // // // // //         setError('Aucun ticket en attente pour ce service');
// // // // // // // // // // //       } else {
// // // // // // // // // // //         setError('Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.');
// // // // // // // // // // //       }
// // // // // // // // // // //     } finally {
// // // // // // // // // // //       setLoading(false);
// // // // // // // // // // //     }
// // // // // // // // // // //   };

// // // // // // // // // // //   // Terminer le service
// // // // // // // // // // //   const completeService = async () => {
// // // // // // // // // // //     if (!activeTicket) return;
    
// // // // // // // // // // //     try {
// // // // // // // // // // //       setLoading(true);
// // // // // // // // // // //       const response = await ticketService.completeTicket(activeTicket.ticketId);
      
// // // // // // // // // // //       if (response.success) {
// // // // // // // // // // //         setActiveTicket(null);
// // // // // // // // // // //         // Mettre à jour les statistiques
// // // // // // // // // // //         setStats(prev => ({
// // // // // // // // // // //           servedClients: prev.servedClients + 1,
// // // // // // // // // // //           averageServiceTime: response.data.serviceTime // Simplifié pour l'exemple
// // // // // // // // // // //         }));
// // // // // // // // // // //       } else {
// // // // // // // // // // //         setError('Le ticket n\'a pas pu être clôturé : il reste en service. Réessayez.');
// // // // // // // // // // //       }
// // // // // // // // // // //     } catch (err) {
// // // // // // // // // // //       console.error('Erreur lors de la fin du service:', err);
// // // // // // // // // // //       setError('Le serveur ne répond pas. Vérifiez la connexion, puis réessayez.');
// // // // // // // // // // //     } finally {
// // // // // // // // // // //       setLoading(false);
// // // // // // // // // // //     }
// // // // // // // // // // //   };

// // // // // // // // // // //   // Si la configuration est en cours de chargement
// // // // // // // // // // //   if (configLoading) {
// // // // // // // // // // //     return (
// // // // // // // // // // //       <div className="min-h-screen flex items-center justify-center bg-gray-100">
// // // // // // // // // // //         <div className="p-8 bg-white rounded-lg shadow-md text-center">
// // // // // // // // // // //           <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
// // // // // // // // // // //           <p className="text-gray-700">Chargement de la configuration...</p>
// // // // // // // // // // //         </div>
// // // // // // // // // // //       </div>
// // // // // // // // // // //     );
// // // // // // // // // // //   }

// // // // // // // // // // //   // Si une erreur s'est produite lors du chargement de la configuration
// // // // // // // // // // //   if (configError || !config) {
// // // // // // // // // // //     return (
// // // // // // // // // // //       <div className="min-h-screen flex items-center justify-center bg-gray-100">
// // // // // // // // // // //         <div className="p-8 bg-white rounded-lg shadow-md text-center max-w-md">
// // // // // // // // // // //           <div className="text-red-500 text-6xl mb-4">⚠️</div>
// // // // // // // // // // //           <h1 className="text-xl font-bold mb-4">Erreur</h1>
// // // // // // // // // // //           <p className="mb-6 text-gray-700">{configError || "La configuration n'a pas pu être chargée"}</p>
// // // // // // // // // // //           <button 
// // // // // // // // // // //             className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
// // // // // // // // // // //             onClick={() => window.location.reload()}
// // // // // // // // // // //           >
// // // // // // // // // // //             Réessayer
// // // // // // // // // // //           </button>
// // // // // // // // // // //         </div>
// // // // // // // // // // //       </div>
// // // // // // // // // // //     );
// // // // // // // // // // //   }

// // // // // // // // // // //   // Écran de connexion au guichet
// // // // // // // // // // //   if (!isLoggedIn) {
// // // // // // // // // // //     return (
// // // // // // // // // // //       <div className="min-h-screen bg-gray-100 p-4">
// // // // // // // // // // //         <div className="max-w-md mx-auto bg-white rounded-lg shadow-md overflow-hidden">
// // // // // // // // // // //           <div className="p-4 bg-blue-600 text-white">
// // // // // // // // // // //             <h1 className="text-xl font-bold">
// // // // // // // // // // //               {config.agency?.name || "Agence"} - Connexion Guichet
// // // // // // // // // // //             </h1>
// // // // // // // // // // //             <p>{config.agency?.company || "Entreprise"}</p>
// // // // // // // // // // //           </div>
          
// // // // // // // // // // //           <div className="p-6">
// // // // // // // // // // //             <h2 className="text-lg font-semibold mb-4">Sélectionnez votre guichet:</h2>
            
// // // // // // // // // // //             <div className="space-y-3">
// // // // // // // // // // //               {config.counters?.map(counter => {
// // // // // // // // // // //                 const serviceType = config.serviceTypes.find(s => s.id === counter.serviceType);
// // // // // // // // // // //                 return (
// // // // // // // // // // //                   <button
// // // // // // // // // // //                     key={counter.id}
// // // // // // // // // // //                     className="w-full p-4 bg-white border border-gray-300 rounded shadow-sm flex justify-between items-center hover:bg-gray-50"
// // // // // // // // // // //                     onClick={() => loginToCounter(counter)}
// // // // // // // // // // //                   >
// // // // // // // // // // //                     <div>
// // // // // // // // // // //                       <span className="font-semibold">{counter.name}</span>
// // // // // // // // // // //                       <span className="ml-2 text-sm">
// // // // // // // // // // //                         ({serviceType?.name || counter.serviceType})
// // // // // // // // // // //                       </span>
// // // // // // // // // // //                     </div>
// // // // // // // // // // //                     <div 
// // // // // // // // // // //                       className="w-4 h-4 rounded-full" 
// // // // // // // // // // //                       style={{ backgroundColor: serviceType?.color || '#999' }}
// // // // // // // // // // //                     ></div>
// // // // // // // // // // //                   </button>
// // // // // // // // // // //                 );
// // // // // // // // // // //               })}
// // // // // // // // // // //             </div>
// // // // // // // // // // //           </div>
// // // // // // // // // // //         </div>
// // // // // // // // // // //       </div>
// // // // // // // // // // //     );
// // // // // // // // // // //   }

// // // // // // // // // // //   // Interface principale du guichet
// // // // // // // // // // //   const currentServiceType  = config.serviceTypes.find(s => s.id === selectedCounter.serviceType);

// // // // // // // // // // //   return (
// // // // // // // // // // //     <div className="min-h-screen bg-gray-100 p-4">
// // // // // // // // // // //       <div className="max-w-md mx-auto">
// // // // // // // // // // //         {/* En-tête */}
// // // // // // // // // // //         <header className="bg-white rounded-lg shadow-md p-4 mb-4 flex justify-between items-center">
// // // // // // // // // // //           <div>
// // // // // // // // // // //             <h1 className="text-xl font-bold">
// // // // // // // // // // //               {selectedCounter.name}
// // // // // // // // // // //             </h1>
// // // // // // // // // // //             <p className="text-sm" style={{ color: currentServiceType ?.color || '#666' }}>
// // // // // // // // // // //               {currentServiceType ?.name || selectedCounter.serviceType}
// // // // // // // // // // //             </p>
// // // // // // // // // // //           </div>
          
// // // // // // // // // // //           <button 
// // // // // // // // // // //             onClick={logout} 
// // // // // // // // // // //             className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
// // // // // // // // // // //           >
// // // // // // // // // // //             Déconnexion
// // // // // // // // // // //           </button>
// // // // // // // // // // //         </header>
        
// // // // // // // // // // //         {/* Message d'erreur */}
// // // // // // // // // // //         {error && (
// // // // // // // // // // //           <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded shadow">
// // // // // // // // // // //             <div className="flex">
// // // // // // // // // // //               <div className="py-1">
// // // // // // // // // // //                 <svg className="w-6 h-6 mr-4" fill="currentColor" viewBox="0 0 20 20">
// // // // // // // // // // //                   <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
// // // // // // // // // // //                 </svg>
// // // // // // // // // // //               </div>
// // // // // // // // // // //               <div>
// // // // // // // // // // //                 <p className="font-bold">Erreur</p>
// // // // // // // // // // //                 <p>{error}</p>
// // // // // // // // // // //               </div>
// // // // // // // // // // //             </div>
// // // // // // // // // // //             <button 
// // // // // // // // // // //               className="mt-2 text-sm underline"
// // // // // // // // // // //               onClick={() => setError(null)}
// // // // // // // // // // //             >
// // // // // // // // // // //               Fermer
// // // // // // // // // // //             </button>
// // // // // // // // // // //           </div>
// // // // // // // // // // //         )}
        
// // // // // // // // // // //         {/* Ticket actif */}
// // // // // // // // // // //         <div className="bg-white rounded-lg shadow-md p-6 mb-4">
// // // // // // // // // // //           <h2 className="text-lg font-semibold mb-4">Ticket en cours</h2>
          
// // // // // // // // // // //           {activeTicket ? (
// // // // // // // // // // //             <div className="flex justify-between items-center">
// // // // // // // // // // //               <div className="flex-1">
// // // // // // // // // // //                 <div className="text-5xl font-bold" style={{ color: currentServiceType ?.color || '#333' }}>
// // // // // // // // // // //                   {activeTicket.ticketId}
// // // // // // // // // // //                 </div>
// // // // // // // // // // //                 <div className="mt-2 text-gray-600">
// // // // // // // // // // //                   {activeTicket.calledAt && (
// // // // // // // // // // //                     `Appelé à ${new Date(activeTicket.calledAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`
// // // // // // // // // // //                   )}
// // // // // // // // // // //                 </div>
// // // // // // // // // // //               </div>
              
// // // // // // // // // // //               <button 
// // // // // // // // // // //                 onClick={completeService}
// // // // // // // // // // //                 disabled={loading}
// // // // // // // // // // //                 className="px-4 py-3 bg-green-500 text-white rounded-lg shadow hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
// // // // // // // // // // //               >
// // // // // // // // // // //                 {loading ? 'Chargement...' : 'Terminer'}
// // // // // // // // // // //               </button>
// // // // // // // // // // //             </div>
// // // // // // // // // // //           ) : (
// // // // // // // // // // //             <div className="text-center py-8 text-gray-500">
// // // // // // // // // // //               Aucun ticket en service
// // // // // // // // // // //             </div>
// // // // // // // // // // //           )}
// // // // // // // // // // //         </div>
        
// // // // // // // // // // //         {/* Contrôles et informations */}
// // // // // // // // // // //         <div className="bg-white rounded-lg shadow-md p-6 mb-4">
// // // // // // // // // // //           <div className="flex justify-between items-center mb-6">
// // // // // // // // // // //             <h2 className="text-lg font-semibold">File d'attente</h2>
// // // // // // // // // // //             <div className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
// // // // // // // // // // //               {waitingCount} en attente
// // // // // // // // // // //             </div>
// // // // // // // // // // //           </div>
          
// // // // // // // // // // //           <button 
// // // // // // // // // // //             onClick={callNextTicket}
// // // // // // // // // // //             disabled={!!activeTicket || waitingCount === 0 || loading}
// // // // // // // // // // //             className={`w-full py-4 rounded-lg shadow text-white text-lg font-semibold 
// // // // // // // // // // //               ${!activeTicket && waitingCount > 0 && !loading
// // // // // // // // // // //                 ? 'bg-blue-500 hover:bg-blue-600' 
// // // // // // // // // // //                 : 'bg-gray-400 cursor-not-allowed'}`}
// // // // // // // // // // //           >
// // // // // // // // // // //             {loading ? 'Chargement...' : 'Appeler le prochain client'}
// // // // // // // // // // //           </button>
          
// // // // // // // // // // //           {waitingCount > 0 && !activeTicket && (
// // // // // // // // // // //             <div className="mt-4 text-sm text-gray-500 text-center">
// // // // // // // // // // //               Prochain ticket: {
// // // // // // // // // // //                 waitingTickets.length > 0 ? 
// // // // // // // // // // //                 waitingTickets[0]?.ticketId : '-'
// // // // // // // // // // //               }
// // // // // // // // // // //             </div>
// // // // // // // // // // //           )}
          
// // // // // // // // // // //           {activeTicket && (
// // // // // // // // // // //             <div className="mt-4 text-sm text-red-500 text-center">
// // // // // // // // // // //               Veuillez terminer le service en cours avant d'appeler le prochain client
// // // // // // // // // // //             </div>
// // // // // // // // // // //           )}
// // // // // // // // // // //         </div>
        
// // // // // // // // // // //         {/* Statistiques */}
// // // // // // // // // // //         <div className="bg-white rounded-lg shadow-md p-4">
// // // // // // // // // // //           <h2 className="text-lg font-semibold mb-3">Statistiques du jour</h2>
          
// // // // // // // // // // //           <div className="grid grid-cols-2 gap-4">
// // // // // // // // // // //             <div className="p-3 bg-blue-50 rounded-md">
// // // // // // // // // // //               <div className="text-sm text-blue-800">Clients servis</div>
// // // // // // // // // // //               <div className="text-2xl font-bold">{stats.servedClients}</div>
// // // // // // // // // // //             </div>
            
// // // // // // // // // // //             <div className="p-3 bg-green-50 rounded-md">
// // // // // // // // // // //               <div className="text-sm text-green-800">Temps moyen</div>
// // // // // // // // // // //               <div className="text-2xl font-bold">
// // // // // // // // // // //                 {stats.averageServiceTime > 0 
// // // // // // // // // // //                   ? `${Math.round(stats.averageServiceTime / 60)} min` 
// // // // // // // // // // //                   : '-'}
// // // // // // // // // // //               </div>
// // // // // // // // // // //             </div>
// // // // // // // // // // //           </div>
// // // // // // // // // // //         </div>
// // // // // // // // // // //       </div>
// // // // // // // // // // //     </div>
// // // // // // // // // // //   );
// // // // // // // // // // // };

// // // // // // // // // // // export default CounterApp;